import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API } from '../App';
import { useAuthedImagePolling, useAuthedImageFirstMatch } from '../hooks/useAuthedImage';
import './ClinicalSummaryPage.css';

/**
 * Affiche une image à partir d'une URL.
 * Certaines de ces images (squelette postural, overlays pieds) sont
 * générées en arrière-plan par gpt-image et peuvent ne pas encore être
 * prêtes si le praticien ouvre le bilan juste après l'analyse : la
 * génération mesurée prend ~60-90s pour la posture (2 vues) et ~90-180s
 * pour les pieds (4 vues), parfois plus en cas de throttling Azure (retries
 * 429). MAX_ATTEMPTS=60 x 5s = 5 min de marge, largement au-dessus du pire
 * cas mesuré.
 *
 * <img src=...> ne peut pas envoyer le jeton d'authentification -- on passe
 * par fetch() (intercepté globalement, lui reçoit le jeton) + URL objet
 * locale, avec re-essai tant que le backend répond 404 (génération en cours).
 */
function OverlayImage({ src, alt, className, t }) {
  const { status, blobUrl } = useAuthedImagePolling(src, { intervalMs: 5000, maxAttempts: 60 });

  return (
    <div className="cs-illustration-wrap">
      {status === 'loading' && (
        <div className="cs-illustration-pending">
          <span className="dot-pulse" />
          {t ? t('summary.image_generating') : 'Génération en cours…'}
        </div>
      )}
      {status === 'failed' && (
        <div className="cs-illustration-pending cs-illustration-failed">
          {t ? t('summary.image_failed') : 'Image indisponible (échec de génération)'}
        </div>
      )}
      {blobUrl && (
        <img
          src={blobUrl}
          alt={alt}
          className={className || 'cs-illustration'}
          style={{ display: status === 'loaded' ? 'block' : 'none' }}
        />
      )}
    </div>
  );
}

/**
 * Essaie les URLs dans l'ordre et affiche la première qui répond.
 * Utile quand on ne sait pas quelle vue (sagittale / frontale) est disponible.
 */
function GaitImage({ urls, alt, className }) {
  const { blobUrl, failed } = useAuthedImageFirstMatch(urls);

  if (failed || !blobUrl) return null;

  return (
    <img
      src={blobUrl}
      alt={alt}
      className={className || 'cs-illustration'}
    />
  );
}

const formatDuration = (ms) => (ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`);

export default function ClinicalSummaryPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [patient,     setPatient]     = useState(null);
  const [summary,     setSummary]     = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [generating,  setGenerating]  = useState(false);
  const [error,       setError]       = useState('');

  // Les deux requêtes sont attendues ensemble : le rendu du bilan a besoin de
  // `patient` pour savoir quelles vues ont été photographiées (cf.
  // hasPosturalView / hasFootView plus bas). Sans ça, le premier rendu se
  // ferait avec patient=null et afficherait un instant des cadres
  // "génération en cours" pour des vues inexistantes.
  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/patients/${id}`)
        .then(r => { if (!r.ok) throw new Error(); return r.json(); })
        .then(setPatient)
        .catch(() => {}),
      fetch(`${API}/api/patients/${id}/clinical-summary`)
        .then(r => { if (!r.ok) throw new Error(); return r.json(); })
        .then(s => setSummary(s.data))
        .catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [id]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    try {
      const lang = (i18n.language || 'fr').split('-')[0];
      const res = await fetch(`${API}/api/patients/${id}/clinical-summary?lang=${lang}`, { method: 'POST' });
      if (!res.ok) throw new Error(t('summary.generate_error'));
      const data = await res.json();
      setSummary(data.data);
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const handlePrint = () => window.print();

  const handleDownload = () => {
    const filename = patient
      ? `bilan_${patient.last_name}_${patient.first_name}`.replace(/\s+/g, '_')
      : 'bilan_clinique';
    const previousTitle = document.title;
    document.title = filename;
    window.print();
    document.title = previousTitle;
  };

  if (loading) return <div className="cs-page"><p className="cs-loading">{t('summary.loading')}</p></div>;

  const fullName = patient ? `${patient.first_name} ${patient.last_name.toUpperCase()}` : '';

  // Historique = toutes les consultations sauf la dernière (= consultation du jour)
  const history = (patient?.consultations || []).slice(0, -1).reverse();

  // Les photos sont optionnelles : le praticien peut n'en prendre qu'une
  // partie. Aucun overlay ne sera JAMAIS généré pour une vue non
  // photographiée (cf. present_views côté backend), donc sonder son URL
  // reviendrait à afficher un cadre "génération en cours…" clignotant pendant
  // les 5 minutes de polling avant de basculer en "image indisponible".
  // On ne rend donc l'illustration que pour les vues réellement présentes.
  const posturalViews = (patient?.photos || []).map(p => p.view);
  const footViews     = (patient?.foot_photos || []).map(p => p.view);
  const hasPosturalView = view => posturalViews.includes(view);
  const hasFootView     = view => footViews.includes(view);

  // URLs de la frame annotée — sagittal en priorité, puis frontal
  const gaitFrameUrls = [
    `${API}/api/patients/${id}/gait-frame/sagittal`,
    `${API}/api/patients/${id}/gait-frame/frontal`,
  ];

  // URLs du graphique — sagittal en priorité (genou/cheville), puis frontal
  const gaitChartUrls = [
    `${API}/api/patients/${id}/gait-chart/sagittal`,
    `${API}/api/patients/${id}/gait-chart/frontal`,
  ];

  return (
    <div className="cs-page">
      <header className="cs-header no-print">
        <button className="back-btn" onClick={() => navigate(`/gait/${id}`)}>
          {t('summary.back_to_gait')}
        </button>
        <div className="cs-title-row">
          <div>
            <p className="cs-eyebrow">{t('summary.eyebrow')}</p>
            <h1 className="cs-title">{t('summary.title')}</h1>
          </div>
          {summary && (
            <div className="cs-actions">
              <button className="btn-secondary" onClick={handleGenerate} disabled={generating}>
                {generating
                  ? <><span className="spinner dark" /> {t('summary.regenerating')}</>
                  : t('summary.regenerate_btn')}
              </button>
              <button className="btn-secondary" onClick={handleDownload}>
                {t('summary.download_btn') || 'Télécharger en PDF'}
              </button>
              <button className="btn-primary" onClick={handlePrint}>
                {t('summary.print_btn')}
              </button>
            </div>
          )}
        </div>
      </header>

      {!summary && (
        <section className="cs-empty no-print">
          <p>{t('summary.empty_text')}</p>
          <button className="btn-primary" onClick={handleGenerate} disabled={generating}>
            {generating
              ? <><span className="spinner dark" /> {t('summary.generating')}</>
              : t('summary.generate_btn')}
          </button>
          {error && <div className="cs-error">{error}</div>}
        </section>
      )}

      {summary && (
        <div className="cs-print-area">
          <div className="cs-print-header">
            <h1>{t('summary.print_title')}</h1>
            {patient && (
              <div className="cs-patient-info">
                <p><strong>{fullName}</strong></p>
                {patient.dob && <p>{t('summary.born_on')} {new Date(patient.dob).toLocaleDateString(i18n.language)}</p>}
                <p>{t('summary.report_date')} : {new Date().toLocaleDateString(i18n.language, { day: '2-digit', month: 'long', year: 'numeric' })}</p>
              </div>
            )}
          </div>

          <section className="cs-section cs-summary">
            <h2>{t('summary.section_summary')}</h2>
            <p>{summary.resume}</p>
          </section>

          <div className="cs-grid">
            <section className="cs-section">
              <h2>{t('summary.section_postural')}</h2>
              <p>{summary.observations_posturales}</p>
              <div className="cs-illustrations">
                {hasPosturalView('back') && (
                  <OverlayImage src={`${API}/api/patients/${id}/postural-overlay/back`} alt={t('summary.alt_back_view')} t={t} />
                )}
                {hasPosturalView('side') && (
                  <OverlayImage src={`${API}/api/patients/${id}/postural-overlay/side`} alt={t('summary.alt_side_view')} t={t} />
                )}
              </div>
              {patient?.postural_overlay_duration_ms != null && (
                <div className="cs-illustration-duration no-print">
                  {t('summary.image_duration', { time: formatDuration(patient.postural_overlay_duration_ms) })}
                </div>
              )}
            </section>

            <section className="cs-section">
              <h2>{t('summary.section_foot')}</h2>
              <p>{summary.observations_podologiques}</p>
              <div className="cs-illustrations">
                {[['heels', t('summary.alt_heels')],
                  ['arch_left', t('summary.alt_arch_left')],
                  ['arch_right', t('summary.alt_arch_right')],
                  ['podoscope', t('summary.alt_podoscope')]]
                  .filter(([view]) => hasFootView(view))
                  .map(([view, alt]) => (
                    <OverlayImage key={view} src={`${API}/api/patients/${id}/foot-overlay/${view}`} alt={alt} t={t} />
                  ))}
              </div>
              {patient?.foot_overlay_duration_ms != null && (
                <div className="cs-illustration-duration no-print">
                  {t('summary.image_duration', { time: formatDuration(patient.foot_overlay_duration_ms) })}
                </div>
              )}
            </section>

            <section className="cs-section">
              <h2>{t('summary.section_gait')}</h2>
              <p>{summary.observations_marche}</p>

              {/*
                Illustrations de la marche.
                GaitImage essaie sagittale en premier, puis frontale.
                Se cache si aucune vidéo n'a été analysée (404 → hidden).
              */}
              <div className="cs-gait-illustrations">
                <div className="cs-gait-frame">
                  <GaitImage
                    urls={gaitFrameUrls}
                    alt={t('summary.alt_gait_frame')}
                    className="cs-illustration cs-illustration--gait-frame"
                  />
                </div>
                <div className="cs-gait-chart">
                  <GaitImage
                    urls={gaitChartUrls}
                    alt={t('summary.alt_gait_chart')}
                    className="cs-illustration cs-illustration--gait-chart"
                  />
                </div>
              </div>
            </section>
          </div>

          {summary.correlation_pied_posture && (
            <section className="cs-section cs-correlation">
              <h2>{t('summary.section_correlation')}</h2>
              <p>{summary.correlation_pied_posture}</p>
            </section>
          )}

          {summary.evolution && (
            <section className="cs-section cs-evolution">
              <h2>{t('summary.section_evolution')}</h2>
              <p>{summary.evolution}</p>
            </section>
          )}

          {summary.analyse_documents_fournis && (
            <section className="cs-section cs-documents">
              <h2>{t('summary.section_documents')}</h2>
              {summary.analyse_documents_fournis.split(/\n+/).filter(Boolean).map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </section>
          )}

          {summary.reponse_prescription && (
            <section className="cs-section cs-prescription">
              <h2>{t('summary.section_prescription')}</h2>
              {summary.reponse_prescription.split(/\n+/).filter(Boolean).map((para, i) => (
                <p key={i}>{para}</p>
              ))}
              {summary.graphiques_marche?.length > 0 && (
                <div className="cs-prescription-charts">
                  {summary.graphiques_marche.map((chart, i) => (
                    <img key={i} src={chart.url} alt={chart.type} className="cs-prescription-chart-img" />
                  ))}
                </div>
              )}
            </section>
          )}

          <section className="cs-section cs-recommendations">
            <h2>{t('summary.section_recommendations')}</h2>
            <ul>
              {(summary.recommandations || []).map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          </section>

          <section className="cs-section cs-followup">
            <h2>{t('summary.section_followup')}</h2>
            <p>{summary.suivi}</p>
          </section>

          {history.length > 0 && (
            <section className="cs-section cs-history">
              <h2>{t('summary.section_history')}</h2>
              {history.map(c => (
                <div key={c.id} className="cs-history-item">
                  <div className="cs-history-date">
                    {new Date(c.date).toLocaleDateString(i18n.language, { day: '2-digit', month: 'long', year: 'numeric' })}
                  </div>
                  <div className="cs-history-details">
                    <span>{t('summary.history_pain')} : {c.pain_level}/10</span>
                    <span>{t('summary.history_handicap')} : {c.handicap}/10</span>
                    {c.pain_zones?.length > 0 && <span>{t('summary.history_zones')} : {c.pain_zones.join(', ')}</span>}
                  </div>
                  {c.notes && <p className="cs-history-notes">{c.notes}</p>}
                </div>
              ))}
            </section>
          )}

          {error && <div className="cs-error no-print">{error}</div>}
        </div>
      )}
    </div>
  );
}